import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { getValidator } from "./create/validator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Identity } from "../../schema/types/identity/entity";
import { identityToInput, identityToPatch, inputToIdentity } from "./utils";
import { useGetIdentityQuery } from "../../store/api/identityApi";
import { IdentityInput } from "../../schema/types/identity/input";
import { IdentityDetailContent } from "./detailContent";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";
import { getDirtyChecker } from "./create/dirtyChecker";

interface Props {
  identity: Identity;
}
export const IdentityDetailForm: React.FC<Props> = ({ identity }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetIdentityQuery(linksOf(identity).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = identityToInput(identity);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: identityToInput(identity) }));
  }, [identity.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: IdentityInput) {
    try {
      let body = identityToPatch(inputToIdentity(input), identity, { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(identity).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Identity is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <IdentityDetailContent identity={identity} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(identity).self!} inputToItemFn={inputToIdentity} />
      <CpPreviewModal inputToItemFn={inputToIdentity} kind="identity" namePath="name" title="Identity" />
      <CpDebugModal inputToItemFn={inputToIdentity} kind="identity" namePath="name" title="Identity" />
    </CpFormContext.Provider>
  );
};
